import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import { Tooltip as RechartsTooltip, TooltipProps } from 'recharts';
import {
  getTooltipContentStyle,
  getTooltipItemStyle,
  getTooltipLabelStyle,
} from '../../constants/chartConfig';

export interface ChartTooltipProps extends Omit<TooltipProps<any, any>, 'contentStyle' | 'itemStyle' | 'labelStyle'> {
  /**
   * Whether to use dark theme styles
   * If not provided, will be inferred from Material-UI theme
   */
  isDark?: boolean;
}

/**
 * Default formatter for tooltip values
 * Returns [formattedValue, name] as expected by Recharts
 */
const defaultFormatter = (value: any, name: string) => {
  if (typeof value === 'number') {
    // Format numbers with up to 2 decimal places
    const formattedValue = value % 1 === 0 ? value.toString() : value.toFixed(2);
    return [formattedValue, name];
  }
  return [value, name];
};

/**
 * Reusable chart tooltip component
 * Provides consistent tooltip styling across all charts
 */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  isDark,
  formatter,
  ...rest
}) => {
  const theme = useTheme();
  const darkMode = isDark !== undefined ? isDark : theme.palette.type === 'dark';

  return (
    <RechartsTooltip
      contentStyle={getTooltipContentStyle(darkMode)}
      itemStyle={getTooltipItemStyle(darkMode)}
      labelStyle={getTooltipLabelStyle(darkMode)}
      wrapperStyle={{ zIndex: 9999, pointerEvents: 'none' }}
      formatter={formatter || defaultFormatter}
      isAnimationActive={true}
      animationDuration={300}
      allowEscapeViewBox={{ x: true, y: true }}
      {...rest}
    />
  );
};
